import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiVisa, EntityLoginEpService } from 'blg-akaun-ts-lib';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { DropDownService } from '../../services/drop-down-service';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, toArray } from 'rxjs/operators';
import { PermissionStates } from '../../modules/permission/permission-controller/root';
import { UserPermInquirySelectors } from '../../modules/permission/user-permissions-inquiry-controller/selectors';
import { SharedUtilitiesHelperFunctions } from '../helper-functions';

@Component({
  selector: 'app-select-multi-branch-drop-down',
  templateUrl: './select-multi-branch-drop-down.component.html',
  styleUrls: ['./select-multi-branch-drop-down.component.css']
})
export class SelectMultiBranchDropDownComponent implements OnInit, OnDestroy {

  @Input() branch: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'Branch';
  @Input() options: any;
  @Input() readPermissionDefintion;
  @Input() companyGuidList: any[];

  @Output() branchSelected = new EventEmitter<any>();

  userPermissionTarget$ = this.permissionStore.select(
    UserPermInquirySelectors.selectUserPermInquiry
  );

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = 'branch';
  allSelected = false;
  filteredCache: any = [];
  branchTargets: string[]=[];

  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;

  protected _onDestroy = new Subject<void>();

  constructor(private dropDownService: DropDownService,
    private entityLoginEpService: EntityLoginEpService,
    private readonly store: Store<SessionStates>,
    private readonly permissionStore: Store<PermissionStates>
  ) {
  }

  ngOnInit() {
    this.key = this.isSupplierAccess() ? 'branch-supplier': 'branch';

    // set initial selection
    this.store.select(SessionSelectors.selectDropDownRowData).subscribe(resolved => {
      this.rowData = resolved?resolved[this.key]?resolved[this.key]:[]:[];
      if (this.rowData.length === 0) {
        if (this.isSupplierAccess()) {
          this.getSupplierAccessBranch();
        }
        else {
          this.getRowData();
        }
      }
      else {
        this.filteredOptions$.next(this.rowData.slice());
      }
    })

    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
        this.setToggleAllCheckboxState();
      });

    // listen for multi select field value changes
    this.branch.valueChanges
      .pipe(takeUntil(this._onDestroy)).subscribe(() => {
        this.setToggleAllCheckboxState();
      });

    this.branchTargets = SharedUtilitiesHelperFunctions.getTargetsByPermission(this.userPermissionTarget$,
      this.readPermissionDefintion, 'bl_fi_mst_branch'
    );
  }

  ngOnChanges() {
    if (this.isFilterCompany()) {
      this.getRowData();
    }
  }

  getRowData() {
    if (this.isFilterCompany() && !this.companyGuidList) return;
    let formData = {
      "limit" : null,
      "orderBy" : "code",
      "order" : "ASC"
    }

    if (this.companyGuidList) {
      Object.assign(formData,
        {
          "filterConditions": [
          {
            "filterColumn": "comp_guid",
            "filterValues": this.companyGuidList,
            "filterOperator": "IN"
          }]
        }
      )
    }
    this.dropDownService.get('branch', formData, this.apiVisa).subscribe(resolved => {
      this.rowData = resolved.data;
      if(this.branchTargets?.length>0){
        this.rowData = this.rowData.filter(record=>this.branchTargets.includes(record.guid))
      }
      this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));
      //fill the drop down
      this.filteredOptions$.next(this.rowData.slice());

      // set default branch
      this.store.select(SessionSelectors.selectPersonalSettings).subscribe({next: (resolve: any) => {
        if (resolve?.DEFAULT_BRANCH) {
          if (this.isMultiple()) {
            this.branch.patchValue([resolve?.DEFAULT_BRANCH]);
          }
          else {
            this.branch.patchValue(resolve?.DEFAULT_BRANCH);
          }
        }
      }});
    });
  }

  getSupplierAccessBranch() {
    this.store.select(SessionSelectors.selectEntityLoginSubjectLink).pipe(
      mergeMap(a => from(a).pipe(
        concatMap(a_a => forkJoin([
          this.entityLoginEpService.getLoginEntityByGuid(a_a.bl_fi_mst_entity_login_subject_link.entity_hdr_guid.toString(), this.apiVisa).pipe(
           catchError((err) => of(err))
          ),
          ]
        ).pipe(
          map(([entityLogin]) => {
            const ext = entityLogin.data.bl_fi_mst_entity_ext.find(x => x.param_code === 'SUPPLIER_ACCESS_BRANCH')??[];
            const value = ext.value_json?ext.value_json.data:[];
            return value;
          })
        )
        ),
        toArray(),
        map(b => {
          let arr = b.flat(1);
          arr = [...new Set(arr)];
          return arr;
        })
      )
      )
    )
    .subscribe(resolved => {
      this.rowData = resolved;
      this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));
      //fill the drop down
      this.filteredOptions$.next(this.rowData.slice());

      // set default branch
      this.store.select(SessionSelectors.selectPersonalSettings).subscribe({next: (resolve: any) => {
        console.log('default branch', resolve)
        if (this.isMultiple()) {
          this.branch.patchValue([resolve?.DEFAULT_BRANCH]);
        }
        else {
          this.branch.patchValue(resolve?.DEFAULT_BRANCH);
        }
      }});
    });
  }

  filtering() {
    if (!this.rowData) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredCache = this.rowData.slice();
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the branch
    this.filteredCache = this.rowData.filter((option) =>
    (option.code ?
      option.code.toLowerCase().indexOf(search) > -1 : null) ||
    (option.name ?
      option.name.toLowerCase().indexOf(search) > -1 : null));

    this.filteredOptions$.next(this.filteredCache);
  }

  onSelect(guid: string) {
    if (guid) {
      this.branch.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.branchSelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isSupplierAccess() {
    let value = this.options['isSupplierAccess'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions$.pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(val => {
        if (selectAllValue) {
          if (this.isSelectObject()) {
            this.branch.patchValue(val);
          }
          else {
            this.branch.patchValue([...val.map(item => item.guid)]);
          }
        } else {
          this.branch.patchValue([]);
        }
      }
    );
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.branch && this.branch.value) {
      this.filteredCache.forEach(el => {
        if (this.branch.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked = filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }

  isSelectObject() {
    let value = this.options['selectObject'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  showValue(option) {
    if (this.isSelectObject()) {
      return option;
    }
    else {
      return option.guid;
    }
  }

  isFilterCompany() {
    let value = this.options['filterCompany'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
